import { useEffect, useRef, useState } from "react";
import Button from "@/components/Button";
import { SkeletonElement } from "@/components/Skeleton";
import useCurrentUser from "@/hooks/useCurrentUser";
import { getBackendUri } from "@/lib/backendSource";
import { sendToRaygun } from "@/lib/raygunUtils";
import { formatDuration } from "../_utils";

type Props = {
  callSid: string;
  hasRecording: boolean;
};

type GrokTranscript = {
  text: string | null;
  language: string | null;
  duration: number | null;
};

type GrokComparison = {
  callSid: string;
  transcript: GrokTranscript;
};

// Backend holds a 55s end-to-end budget; the client backstop sits above it.
const REQUEST_TIMEOUT_MS = 70_000;
// Generous guard: an hour of speech transcribes to roughly 50KB.
const MAX_TRANSCRIPT_CHARS = 1_000_000;

export function grokTranscriptionPath(callSid: string): string {
  return `/backoffice/twilio_flex_calls/${encodeURIComponent(callSid)}/grok_transcription`;
}

function friendlyGrokError(status: number | null): string {
  if (status === 401) return "Session expired. Refresh the page and try again.";
  if (status === 404) return "No recording found for this call.";
  if (status === 503) return "Grok transcription isn't available right now. Try again later.";
  return "Grok transcription failed. Try again.";
}

// Routine conditions (expired session, missing recording, unconfigured or
// busy service) are user-facing only, not Raygun defects.
function isExpectedStatus(status: number | null): boolean {
  return status === 401 || status === 404 || status === 503;
}

function parseGrokTranscript(payload: unknown): GrokTranscript | null {
  if (typeof payload !== "object" || payload === null) return null;
  const { text, language, duration } = payload as Record<string, unknown>;
  if (text !== null && typeof text !== "string") return null;
  if (language !== null && language !== undefined && typeof language !== "string") return null;
  if (duration !== null && duration !== undefined && (typeof duration !== "number" || !Number.isFinite(duration))) {
    return null;
  }
  return {
    text: typeof text === "string" ? text : null,
    language: typeof language === "string" ? language : null,
    duration: typeof duration === "number" ? duration : null
  };
}

function comparisonMeta(result: GrokTranscript): string | null {
  const parts = ["Grok transcript"];
  if (result.language) parts.push(result.language);
  if (result.duration != null) parts.push(formatDuration(result.duration));
  return parts.length > 1 ? parts.join(" · ") : null;
}

export default function GrokTranscriptCompare({ callSid, hasRecording }: Props) {
  const { getAccessToken } = useCurrentUser();
  // getAccessToken identity changes every render in Okta/token auth; read it via ref at call time.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const abortRef = useRef<AbortController | null>(null);
  const timeoutIdRef = useRef<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GrokComparison | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clearPendingTimeout = () => {
    if (timeoutIdRef.current !== null) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  // A mounted instance is tied to one call: when identity or recording
  // availability changes, drop any in-flight request and its result so a
  // stale transcript can never render under the new call.
  useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (timeoutIdRef.current !== null) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    setLoading(false);
    setResult(null);
    setError(null);
  }, [callSid, hasRecording]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (timeoutIdRef.current !== null) window.clearTimeout(timeoutIdRef.current);
    },
    []
  );

  const handleCompare = async () => {
    abortRef.current?.abort();
    clearPendingTimeout();
    const controller = new AbortController();
    abortRef.current = controller;
    // Per-request timeout state: a stale request's cleanup must never touch
    // the timer or flags of a newer request.
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    timeoutIdRef.current = timeoutId;
    setLoading(true);
    setResult(null);
    setError(null);

    let status: number | null = null;
    try {
      const token = await getAccessTokenRef.current();
      const response = await fetch(`${getBackendUri()}${grokTranscriptionPath(callSid)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      status = response.status;
      if (!response.ok) {
        throw new Error(`Grok transcription returned HTTP ${response.status}`);
      }
      const transcript = parseGrokTranscript(await response.json());
      if (transcript === null) {
        throw new Error("Grok transcription returned an unexpected response shape");
      }
      if (transcript.text !== null && transcript.text.length > MAX_TRANSCRIPT_CHARS) {
        throw new Error("Grok transcription returned an oversized transcript");
      }
      if (controller.signal.aborted) return;
      setResult({ callSid, transcript });
    } catch (caught) {
      if (controller.signal.aborted && !timedOut) return;
      if (timedOut || !isExpectedStatus(status)) {
        sendToRaygun(
          new Error(
            `Grok transcription ${timedOut ? "timed out" : "failed"} for call ${callSid}: ${
              caught instanceof Error ? caught.message : "unknown error"
            }`
          ),
          { tags: ["call-topics", "grok-transcription"] }
        );
      }
      setError(timedOut ? "Grok transcription timed out. Try again." : friendlyGrokError(status));
    } finally {
      if (timeoutIdRef.current === timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutIdRef.current = null;
      }
      if (!controller.signal.aborted || timedOut) setLoading(false);
    }
  };

  // Gate rendering on identity: the reset above runs in a passive effect, so
  // without this the previous call's transcript could flash under the new call.
  const visibleResult = result && result.callSid === callSid ? result.transcript : null;
  const meta = visibleResult ? comparisonMeta(visibleResult) : null;

  return (
    <section aria-label="Grok transcript comparison" className="flex flex-col gap-2">
      <h3 className="text-title-small text-sys-brand-on-surface">Experimental: Grok comparison</h3>
      <p className="text-body-medium text-sys-brand-on-surface-variant">
        Transcribes this call&apos;s recording with Grok Voice Transcribe 2.0 on demand. Nothing is saved — compare the
        result against the Twilio transcript above.
      </p>
      {!hasRecording ? (
        <p className="text-body-medium text-sys-brand-on-surface-variant">Grok comparison needs a call recording.</p>
      ) : (
        <div className="flex flex-col items-start gap-2">
          <Button
            variant="outlined"
            loading={loading}
            loadingLabel="Transcribing with Grok..."
            onClick={handleCompare}
            testId="grok-transcript-compare-button"
          >
            Compare vs Grok Voice Transcribe 2.0
          </Button>
          {loading && <SkeletonElement height="h-4" width="w-full" rounded="rounded-full" />}
          {error && (
            <p role="alert" className="text-body-medium text-sys-brand-on-surface-variant">
              {error}
            </p>
          )}
          {visibleResult && (
            <div className="flex w-full flex-col gap-2" aria-live="polite">
              {meta && (
                <p className="text-body-medium text-sys-brand-on-surface-variant" data-testid="grok-transcript-meta">
                  {meta}
                </p>
              )}
              <p
                tabIndex={0}
                data-testid="grok-transcript-text"
                className="text-body-medium text-sys-brand-on-surface-variant max-h-48 overflow-y-auto whitespace-pre-wrap"
              >
                {visibleResult.text || "Grok returned no transcript text."}
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
